import React from "react";
import SummaryRow from "./SummaryRow";
import * as utils from "common/utils";
import * as futils from "containers/BudgetHome/Fertiliser/_utils";
import MonthlySummary from "./MonthlySummary";
import Alert from "components/Alert";
import FileSummary from "./FileSummary";
import Watermark from "components/Watermark";

const getTableRowClass = (rowType) => {
    if (rowType === "N") return "Table-tr--nloss";

    if (rowType === "P") return "Table-tr--ploss";

    if (rowType === "K") return "Table-tr--kloss";

    if (rowType === "S") return "Table-tr--sloss";

    return ``;
};

const GetFertiliserNutrients = (block, area, nutrient) => {
    const fertiliserBudget = block.currentResults.nutrientBudget.nutrientCategories && block.currentResults.nutrientBudget.nutrientCategories.Fertiliser;

    const total = fertiliserBudget && fertiliserBudget.total.nutrients[nutrient] ? fertiliserBudget.total.nutrients[nutrient] * area : 0;
    const Synthetic = fertiliserBudget && fertiliserBudget.subTotals.Nutrients.nutrients[nutrient] ? fertiliserBudget.subTotals.Nutrients.nutrients[nutrient] * area : 0;
    const Organic = total - Synthetic;
    return { Synthetic, Organic };
};

const UpdateForBlockNutrientAndType = (block, area, nutrient, fertiliserType, records, fertiliserApplied) => {
    var summary = records.find((s) => s.blockType === block.type && s.fertiliserType === fertiliserType && s.nutrient === nutrient);

    if (summary == null) {
        summary = { blockType: block.type, nutrient: nutrient, fertiliserType: fertiliserType, total: 0, area: 0 };
        records.push(summary);
    }

    if (fertiliserApplied && fertiliserApplied[fertiliserType]) {
        summary.total += fertiliserApplied[fertiliserType];
        summary.area += area;
    }
};

const UpdateForBlock = (block, area, records) => {
    const nFertiliserApplied = GetFertiliserNutrients(block, area, "N");
    UpdateForBlockNutrientAndType(block, area, "N", "Synthetic", records, nFertiliserApplied);
    UpdateForBlockNutrientAndType(block, area, "N", "Organic", records, nFertiliserApplied);
    const pFertiliserApplied = GetFertiliserNutrients(block, area, "P");
    UpdateForBlockNutrientAndType(block, area, "P", "Synthetic", records, pFertiliserApplied);
    UpdateForBlockNutrientAndType(block, area, "P", "Organic", records, pFertiliserApplied);
};

const FertiliserNutrientsApplied = (budget) => {
    const fodderDetails = utils.getFodderBlockAreas(budget.blocks);

    return budget.blocks
        .filter((b) => b.isProductive)
        .reduce((t, b) => {
            const area = futils.GetProductiveBlockArea(b, fodderDetails);
            UpdateForBlock(b, area, t);
            return t;
        }, []);
};

const Fertiliser = ({ before, after, refData, showFileComparison = true }) => {
    const scenarios = [after];
    if (before) scenarios.unshift(before);

    const hasResults = after.details.currentResults && after.details.nLossPerHa && (!before || (before.details.currentResults && before.details.nLossPerHa));

    if (!hasResults) {
        return (
            <div className="u-pt-lg">
                <Alert text="Analyses must have modelled results to show fertiliser information." type="warning" />
            </div>
        );
    }

    const farmNutrientTotals = FertiliserNutrientsApplied(after.details);
    const baseNutrientTotals = before && FertiliserNutrientsApplied(before.details);
    const farmNutrientTotalsAreaSum = farmNutrientTotals.reduce((t, n) => t + n.area, 0);
    const baseFarmNutrientTotalsAreaSum = before ? baseNutrientTotals.reduce((t, n) => t + n.area, 0) : 0;

    if (farmNutrientTotalsAreaSum + baseFarmNutrientTotalsAreaSum === 0) {
        return (
            <div className="u-pt-lg">
                <Alert text="The selected analysis does not contain fertiliser information" type="Info" />
            </div>
        );
    }

    return (
        <div className="ReportPage">
            {showFileComparison && before && <FileSummary before={before} after={after} mini={true} />}
            <FertiliserSummary before={before} after={after} refData={refData} farmNutrientTotals={farmNutrientTotals} baseNutrientTotals={baseNutrientTotals} />
            {false && <FertiliserExtraSummary before={before} after={after} refData={refData} farmNutrientTotals={farmNutrientTotals} baseNutrientTotals={baseNutrientTotals} />}
            <div className="ReportSection u-page-break-avoid">
                <h1>Fertiliser Nutrients by Month</h1>
                <MonthlySummary scenarios={scenarios} categoryName="Fertiliser" rowName="N" />
                <MonthlySummary scenarios={scenarios} categoryName="Fertiliser" rowName="P" />
                <MonthlySummary scenarios={scenarios} categoryName="Fertiliser" rowName="K" />
                <MonthlySummary scenarios={scenarios} categoryName="Fertiliser" rowName="S" />
            </div>
        </div>
    );
};

export default Fertiliser;

export const FertiliserSummary = ({ before, after, farmNutrientTotals, baseNutrientTotals, refData }) => {
    const hasResults = after.details.currentResults && after.details.nLossPerHa && (!before || (before.details.currentResults && before.details.nLossPerHa));
    const noFert = (!after.details.fertiliser || after.details.fertiliser.length === 0) && (!before || !before.details.fertiliser || before.details.fertiliser.length === 0);

    if (noFert) {
        return null;
    }

    if (!hasResults) {
        return (
            <div className="ReportSection">
                <h1>Fertiliser</h1>
                <div className="u-pt-lg">
                    <Alert text="Analyses must have modelled results to show fertiliser information." type="warning" />
                </div>
            </div>
        );
    }

    if (!farmNutrientTotals) {
        farmNutrientTotals = FertiliserNutrientsApplied(after.details);
        baseNutrientTotals = before && FertiliserNutrientsApplied(before.details);
    }
    const blockTypes = new Set(farmNutrientTotals.map((bt) => bt.blockType).concat(baseNutrientTotals ? baseNutrientTotals.map((bt) => bt.blockType) : []));

    return (
        <div className="ReportSection">
            <h1>Fertiliser</h1>
            <div className="Table Table-Compressed">
                <Watermark />

                <table>
                    <thead>
                        <tr>
                            <th data-width="25"></th>
                            <th></th>
                            {before && (
                                <th className="u-textCenter u-before" data-width="12.5">
                                    {utils.truncateText(before.displayName, 35)}
                                </th>
                            )}
                            <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                {utils.truncateText(after.displayName, 35)}
                            </th>
                            {before && (
                                <th className="u-textCenter" data-width="16.7">
                                    Change
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {["N", "P"].map((nutrient) => {
                            return ["Synthetic", "Organic"].map((fertiliserType) => {
                                return Array.from(blockTypes).map((bt, i) => {
                                    const typeLabel = utils.valueToText(refData.blockTypes, bt);
                                    const baseValue = baseNutrientTotals && (baseNutrientTotals.find((bnt) => bnt.blockType === bt && bnt.fertiliserType === fertiliserType && bnt.nutrient === nutrient) || { total: 0, area: 0 });
                                    const actualValue = farmNutrientTotals.find((bnt) => bnt.blockType === bt && bnt.fertiliserType === fertiliserType && bnt.nutrient === nutrient) || { total: 0, area: 0 };

                                    return (
                                        <React.Fragment key={bt}>
                                            <SummaryRow rowClass={`${getTableRowClass(nutrient)} Table-tr--end`} baseValue={baseValue && baseValue.total} actualValue={actualValue && actualValue.total} category={`${fertiliserType} ${nutrient}`} label={typeLabel} unit="kg" />
                                            <SummaryRow rowClass={`${getTableRowClass(nutrient)} Table-tr--end`} baseValue={baseValue && baseValue.area && utils.round(baseValue.total / baseValue.area, 0)} actualValue={actualValue && actualValue.area && utils.round(actualValue.total / actualValue.area, 0)} category=" " label={typeLabel} unit="kg/ha" />
                                        </React.Fragment>
                                    );
                                });
                            });
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const FertiliserExtraSummary = ({ before, after, refData }) => {
    const allBlocks = [];

    const combineBlocks = (budget, key) => {
        const { blocks = [] } = budget;
        blocks
            .filter((b) => b.currentResults && b.currentResults.nutrientBudget && b.currentResults.nutrientBudget.nutrientCategories && b.currentResults.nutrientBudget.nutrientCategories.Fertiliser && b.currentResults.nutrientBudget.nutrientCategories.Fertiliser.total && b.currentResults.nutrientBudget.nutrientCategories.Fertiliser.total.nutrients)
            .forEach((block) => {
                const blockType = block.type === "ProductivePasture" && (!block.animals || block.animals.length === 0) ? "CutAndCarry" : block.type;
                const name = blockType === "CutAndCarry" ? "Cut and carry" : utils.valueToText(refData.blockTypes, blockType);

                var allBlock = allBlocks.find((ab) => ab.type === blockType);
                if (!allBlock) {
                    allBlock = {
                        type: blockType,
                        colour: utils.GetBlockColour(blockType),
                        name: name,
                        actual: {
                            nLoss: 0,
                            pLoss: 0,
                            area: 0,
                        },
                        base: {
                            nLoss: 0,
                            pLoss: 0,
                            area: 0,
                        },
                    };
                    allBlocks.push(allBlock);
                }
                const area = block.type === "FodderCrop" ? block.rotationArea : block.areaInHectares;

                const totalNutrients = block.currentResults.nutrientBudget.nutrientCategories.Fertiliser.total.nutrients;

                if (totalNutrients) {
                    allBlock[key].nLoss += (totalNutrients.N || 0) * area;
                    allBlock[key].pLoss += (totalNutrients.P || 0) * area;
                    allBlock[key].area += area;
                }
            });
    };

    combineBlocks(after.details, "actual");
    if (before) {
        combineBlocks(before.details, "base");
    }
    allBlocks.sort((a, b) => (utils.GetBlockOrder(a.type) > utils.GetBlockOrder(b.type) ? 1 : -1));

    const CalcValues = ({ srcKey, render, blockType }) => {
        const actualValue = utils.round(blockType.actual[srcKey] / blockType.actual.area, 0);
        const baseValue = before && utils.round(blockType.base[srcKey] / blockType.base.area, 0);
        return render(baseValue, actualValue);
    };

    return (
        <div className="ReportSection">
            <h1>Fertiliser Nutrients by Block Type</h1>
            <div className="Table Table-Compressed">
                <Watermark />
                <table>
                    <thead>
                        <tr>
                            <th data-width="25"></th>
                            <th></th>
                            {before && (
                                <th className="u-textCenter u-before" data-width="12.5">
                                    {utils.truncateText(before.displayName, 35)}
                                </th>
                            )}
                            <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                {utils.truncateText(after.displayName, 35)}
                            </th>
                            {before && (
                                <th className="u-textCenter" data-width="16.7">
                                    Change
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {allBlocks.map((b) => {
                            return (
                                <React.Fragment key={b.type}>
                                    <CalcValues srcKey="nLoss" blockType={b} render={(baseValue, actualValue) => <SummaryRow rowClass={`${getTableRowClass("N")} Table-tr--end`} baseValue={baseValue} actualValue={actualValue} category={b.name} prefix={b.name} label="N" unit="kg/ha" />} />
                                    <CalcValues srcKey="pLoss" blockType={b} render={(baseValue, actualValue) => <SummaryRow rowClass={`${getTableRowClass("P")} Table-tr--end`} baseValue={baseValue} actualValue={actualValue} category=" " prefix={b.name} label="P" unit="kg/ha" />} />
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
